import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Star } from 'lucide-react';
import { toast } from '@/lib/toast';
import { ResponsiveDialog } from '@/components/ResponsiveDialog/ResponsiveDialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useSubmitZigBotFeedbackMutation } from '@/services/ApiService';
import { cn } from '@/lib/utils';

export interface ZigBotFeedbackDialogProps {
    isOpen: boolean;
    onClose: () => void;
}

const STAR_VALUES = [1, 2, 3, 4, 5] as const;

export const ZigBotFeedbackDialog = ({ isOpen, onClose }: ZigBotFeedbackDialogProps) => {
    const { t } = useTranslation();
    const [rating, setRating] = useState<number | undefined>(undefined);
    const [comment, setComment] = useState('');
    const [submitFeedback, { isLoading: isSubmitting }] = useSubmitZigBotFeedbackMutation();

    const handleClose = useCallback(() => {
        setRating(undefined);
        setComment('');
        onClose();
    }, [onClose]);

    const handleStarClick = useCallback((value: number) => {
        setRating(prev => (prev === value ? undefined : value));
    }, []);

    const trimmedComment = comment.trim();
    const submitDisabled = isSubmitting || (rating === undefined && trimmedComment.length === 0);

    const handleSubmit = useCallback(async () => {
        try {
            await submitFeedback({
                rating,
                comment: trimmedComment || undefined,
            }).unwrap();
            toast.success(t('chat.feedback.success'));
            handleClose();
        }
        catch {
            toast.error(t('chat.feedback.error'));
        }
    }, [submitFeedback, rating, trimmedComment, handleClose, t]);

    return (
        <ResponsiveDialog
            title={t('chat.feedback.title')}
            subtitle={t('chat.feedback.subtitle')}
            isOpen={isOpen}
            onClose={handleClose}
            footer={(
                <Button
                    variant="primary"
                    className="w-full"
                    size="lg"
                    onClick={handleSubmit}
                    disabled={submitDisabled}
                >
                    {isSubmitting ? t('chat.feedback.submitting') : t('chat.feedback.submit')}
                </Button>
            )}
        >
            <div className="space-y-4">
                <div>
                    <p className="text-sm font-medium mb-2">{t('chat.feedback.ratingQuestion')}</p>
                    <div className="flex items-center gap-1" role="radiogroup" aria-label={t('chat.feedback.ratingLabel')}>
                        {STAR_VALUES.map((value) => {
                            const filled = rating !== undefined && value <= rating;
                            return (
                                <button
                                    key={value}
                                    type="button"
                                    role="radio"
                                    aria-checked={rating === value}
                                    aria-label={value === 1 ? t('chat.feedback.oneStar') : t('chat.feedback.stars', { value })}
                                    onClick={() => handleStarClick(value)}
                                    className="p-1 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
                                >
                                    <Star
                                        className={cn(
                                            'size-7 transition-colors',
                                            filled ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground',
                                        )}
                                    />
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div>
                    <label htmlFor="zigbot-feedback-comment" className="text-sm font-medium mb-2 block">
                        {t('chat.feedback.comments')}
                        <span className="ml-2 text-sm font-normal text-gray-dark">{t('chat.feedback.optional')}</span>
                    </label>
                    <Textarea
                        id="zigbot-feedback-comment"
                        value={comment}
                        onChange={e => setComment(e.target.value)}
                        placeholder={t('chat.feedback.commentPlaceholder')}
                        rows={5}
                    />
                </div>
            </div>
        </ResponsiveDialog>
    );
};
